/** WhatsApp brand green — the single sanctioned hard-coded hex in this section
 * (the channel indicator must read as WhatsApp regardless of theme). Everything
 * else is themed via design tokens. */
export const WHATSAPP_GREEN = '#25D366'

/** Inbox page size for the cursor-paginated `conversations` query. */
export const CONVERSATIONS_PAGE_SIZE = 30

/** Thread page size for the cursor-paginated `conversationMessages` query. */
export const MESSAGES_PAGE_SIZE = 40

/** The 24h customer-service window is shown as "closing" within this margin. */
export const WINDOW_CLOSING_THRESHOLD_MS = 60 * 60 * 1000

/**
 * After our last outbound goes this long without a customer reply, the open
 * thread shows a soft "awaiting customer — consider a follow-up" hint. This is a
 * client-only, computed affordance (no write path); it is the customer-silent
 * counterpart to the server watchdog's we-owe-a-reply `needs-attention` tag,
 * deliberately a longer threshold since it is the customer's turn, not ours.
 */
export const AWAITING_CUSTOMER_AFTER_MS = 3 * 60 * 60 * 1000

/** De-dupe identical rapid sends within this window (double-click guard). */
export const SEND_DEDUPE_WINDOW_MS = 1600

/** Layout dimensions for the three-pane workspace (px). */
export const INBOX_PANE_WIDTH = 332
export const CONTEXT_PANE_WIDTH = 320

/** Cache id prefix for optimistic (not-yet-confirmed) outbound messages. The
 * thread merge policy reconciles an optimistic bubble with its server message
 * by matching `clientMsgId` and preferring the non-optimistic id. */
export const OPTIMISTIC_ID_PREFIX = 'optimistic:'

/**
 * Is this id a client-minted placeholder rather than a server id? Anything
 * that sends an id BACK to the server has to ask: the media read did not, and
 * every optimistic attachment bubble spent a round trip being 400'd
 * (`optimistic:<clientMsgId>` is not a uuid) until its real id arrived.
 */
export const isOptimisticId = (id: unknown): boolean =>
  typeof id === 'string' && id.startsWith(OPTIMISTIC_ID_PREFIX)

/** Sentinel `seq` base so optimistic messages sort to the bottom (newest) until
 * the server assigns a real seq. */
export const OPTIMISTIC_SEQ_BASE = 1e15

/** Dev-only: simulate a live backend (delivery-state progression + inbound). */
export const isMessagingSimulationEnabled = Boolean(
  import.meta.env.DEV &&
  (import.meta.env as unknown as Record<string, string | undefined>).VITE_MESSAGING_SIMULATE ===
    'true',
)
